import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createTaskCN, checkDeletedDuplicateTask, restoreTaskCN } from 'slices/taskCNSlice'
import axios from 'axios'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'
import { Switch } from 'components/ui/switch'
import { ListTodo, UserCircle } from 'lucide-react'
import DeletedDuplicateDialog from 'components/popups/DeletedDuplicateDialog'

// Format project display label: "Project Name - Asset Name"
function formatProjectLabel(project: any): string {
	if (!project) return ''
	const assetName = project.projectAsset?.assetName
	return assetName ? `${project.projectName} - ${assetName}` : project.projectName
}

const PRIORITY_OPTIONS = [
	{ value: 'P1 - Urgent', label: 'P1 - Urgent' },
	{ value: 'P2 - High', label: 'P2 - High' },
	{ value: 'P3 - Medium', label: 'P3 - Medium' },
	{ value: 'P4 - Low', label: 'P4 - Low' },
]

const EFFORT_OPTIONS = [
	{ value: 'XS - Trivial', label: 'XS - Trivial' },
	{ value: 'S - Small', label: 'S - Small' },
	{ value: 'M - Medium', label: 'M - Medium' },
	{ value: 'L - Large', label: 'L - Large' },
	{ value: 'XL - Epic', label: 'XL - Epic' },
]

interface CreateTaskCNModalProps {
	open: boolean
	onClose: () => void
	defaultProjectId?: string
	defaultPortfolioId?: string
}

const CreateTaskCNModal = ({ open, onClose, defaultProjectId, defaultPortfolioId }: CreateTaskCNModalProps) => {
	const dispatch = useDispatch()
	const { userInfo } = useSelector((state: any) => state.userLogin)
	const [projects, setProjects] = useState<any[]>([])

	const now = new Date()
	const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

	const [taskName, setTaskName] = useState('')
	const [description, setDescription] = useState('')
	const [taskPriority, setTaskPriority] = useState('P3 - Medium')
	const [taskEffort, setTaskEffort] = useState('M - Medium')
	const [projectid, setProjectid] = useState(defaultProjectId || '')
	const [assignedTo, setAssignedTo] = useState(userInfo?._id || '')
	const [dateDue, setDateDue] = useState(today)
	const [hasScheduledTime, setHasScheduledTime] = useState(false)
	const [scheduledTime, setScheduledTime] = useState('09:00')
	const [predecessorTaskId, setPredecessorTaskId] = useState('')
	const [candidatePredecessors, setCandidatePredecessors] = useState<any[]>([])
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)
	const [duplicateMatch, setDuplicateMatch] = useState<{ name: string; id: string } | null>(null)
	const [pendingTaskPayload, setPendingTaskPayload] = useState<any>(null)

	// Fetch projects directly
	useEffect(() => {
		if (open && userInfo?.token) {
			axios
				.get('/api/projects/myProjects', { headers: { Authorization: `Bearer ${userInfo.token}` } })
				.then(res => {
					setProjects((res.data || []).filter((p: any) => p.isActive !== false))
				})
				.catch(() => {})
		}
	}, [open, userInfo])

	// Fetch the user's assigned tasks to populate the predecessor dropdown.
	useEffect(() => {
		if (open && userInfo?.token && userInfo?._id) {
			axios
				.get('/api/taskcns/myassigned', {
					params: { assignedTo: userInfo._id },
					headers: { Authorization: `Bearer ${userInfo.token}` },
				})
				.then(res => setCandidatePredecessors(res.data || []))
				.catch(() => setCandidatePredecessors([]))
		}
	}, [open, userInfo])

	// When the project changes, narrow the predecessor list to tasks in the same
	// project. If the current predecessor falls out of scope, clear it.
	const predecessorOptions = useMemo(() => {
		if (!projectid) return candidatePredecessors
		return candidatePredecessors.filter((t: any) => {
			const projId = typeof t.projectid === 'object' ? t.projectid?._id : t.projectid
			return projId === projectid
		})
	}, [candidatePredecessors, projectid])

	useEffect(() => {
		if (predecessorTaskId && !predecessorOptions.some((t: any) => t._id === predecessorTaskId)) {
			setPredecessorTaskId('')
		}
	}, [predecessorOptions, predecessorTaskId])

	// Build assignable users list from selected project's partners + current user
	const assignableUsers = useMemo(() => {
		const users: { id: string; name: string }[] = []
		// Always include current user first
		if (userInfo) {
			users.push({ id: userInfo._id, name: userInfo.name })
		}
		// Add project owner and accepted partners
		const selectedProject = projects.find((p: any) => p._id === projectid)
		if (selectedProject) {
			// Add owner if different from current user
			if (selectedProject.owner && selectedProject.owner._id !== userInfo?._id) {
				users.push({ id: selectedProject.owner._id, name: selectedProject.owner.name })
			}
			// Add accepted partners
			if (Array.isArray(selectedProject.partners)) {
				selectedProject.partners.forEach((p: any) => {
					const partnerId = p.partnerUserId?._id
					if (partnerId && !users.some(u => u.id === partnerId)) {
						users.push({ id: partnerId, name: p.partnerUserId?.name || 'Unknown' })
					}
				})
			}
		}
		return users
	}, [projects, projectid, userInfo])

	const buildTaskPayload = () => {
		const selectedProject = projects.find((p: any) => p._id === projectid)
		const packageid = selectedProject?.projectAsset?._id || selectedProject?.projectAsset
		const packageType = selectedProject?.projectAsset?.assetType || selectedProject?.assetType || ''

		// When the user has set a specific time, build dateDue from the chosen
		// LOCAL date+time and let the browser convert to ISO/UTC. Otherwise keep
		// the legacy noon-UTC sentinel so existing date-only behavior is unchanged.
		const dueIso = dateDue
			? hasScheduledTime
				? new Date(`${dateDue}T${scheduledTime}:00`).toISOString()
				: `${dateDue}T12:00:00.000Z`
			: undefined

		return {
			userid: userInfo._id,
			packageid: packageid || undefined,
			packageType: packageType || undefined,
			portfolioid: defaultPortfolioId || undefined,
			projectid: projectid || undefined,
			taskName: taskName.trim(),
			description: description.trim() || undefined,
			taskPriority,
			taskEffort,
			dateDue: dueIso,
			hasScheduledTime,
			predecessorTaskId: predecessorTaskId || null,
			assignedTo: assignedTo || userInfo._id,
		}
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!taskName.trim()) return

		setIsSubmitting(true)
		try {
			const result = await dispatch(
				checkDeletedDuplicateTask({ taskName: taskName.trim(), projectid }) as any,
			)

			if (result.payload?.hasDuplicate) {
				const deleted = result.payload.deletedItem
				setDuplicateMatch({ name: deleted.taskName, id: deleted._id })
				setPendingTaskPayload(buildTaskPayload())
				setDuplicateDialogOpen(true)
			} else {
				await dispatch(createTaskCN(buildTaskPayload()) as any)
				handleClose()
			}
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleRestoreExisting = async () => {
		if (!duplicateMatch) return
		setIsSubmitting(true)
		try {
			await dispatch(restoreTaskCN(duplicateMatch.id) as any)
			setDuplicateDialogOpen(false)
			setDuplicateMatch(null)
			setPendingTaskPayload(null)
			handleClose()
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleCreateNew = async () => {
		if (!pendingTaskPayload) return
		setIsSubmitting(true)
		try {
			await dispatch(createTaskCN(pendingTaskPayload) as any)
			setDuplicateDialogOpen(false)
			setDuplicateMatch(null)
			setPendingTaskPayload(null)
			handleClose()
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleClose = () => {
		setTaskName('')
		setDescription('')
		setTaskPriority('P3 - Medium')
		setTaskEffort('M - Medium')
		setProjectid(defaultProjectId || '')
		setAssignedTo(userInfo?._id || '')
		setDateDue(today)
		setHasScheduledTime(false)
		setScheduledTime('09:00')
		setPredecessorTaskId('')
		setDuplicateDialogOpen(false)
		setDuplicateMatch(null)
		setPendingTaskPayload(null)
		onClose()
	}

	return (
		<>
		<Dialog open={open} onClose={handleClose}>
			<DialogContent>
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
							<ListTodo className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>Create New Task</DialogTitle>
							<DialogDescription>Add a task to your workspace</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<form onSubmit={handleSubmit} className="p-5 space-y-4">
					{/* Task Title */}
					<div className="space-y-1.5">
						<Label htmlFor="taskName">
							Task Title <span className="text-red-500">*</span>
						</Label>
						<Input
							id="taskName"
							type="text"
							value={taskName}
							onChange={e => setTaskName(e.target.value)}
							placeholder="e.g., Review proposal, Send follow-up email"
							autoFocus
							required
						/>
					</div>

					{/* Priority and Effort row */}
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-1.5">
							<Label htmlFor="taskPriority">Priority</Label>
							<Select
								id="taskPriority"
								value={taskPriority}
								onChange={e => setTaskPriority(e.target.value)}
							>
								{PRIORITY_OPTIONS.map(opt => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="taskEffort">Effort</Label>
							<Select
								id="taskEffort"
								value={taskEffort}
								onChange={e => setTaskEffort(e.target.value)}
							>
								{EFFORT_OPTIONS.map(opt => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
						</div>
					</div>

					{/* Project and Due Date row */}
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-1.5">
							<Label htmlFor="projectid">
								Project <span className="text-red-500">*</span>
							</Label>
							<Select
								id="projectid"
								value={projectid}
								onChange={e => setProjectid(e.target.value)}
								disabled={!!defaultProjectId}
							>
								<option value="">Select a project</option>
								{projects.map((project: any) => (
									<option key={project._id} value={project._id}>
										{formatProjectLabel(project)}
									</option>
								))}
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="dateDue">Due Date</Label>
							<Input
								id="dateDue"
								type="date"
								value={dateDue}
								onChange={e => setDateDue(e.target.value)}
							/>
						</div>
					</div>

					{/* Scheduled time toggle + input */}
					<div className="grid grid-cols-2 gap-4 items-end">
						<label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
							<Switch
								checked={hasScheduledTime}
								onChange={e => setHasScheduledTime(e.target.checked)}
							/>
							<span>Set specific time</span>
						</label>
						<div className="space-y-1.5">
							<Label htmlFor="scheduledTime">Time</Label>
							<Input
								id="scheduledTime"
								type="time"
								value={scheduledTime}
								onChange={e => setScheduledTime(e.target.value)}
								disabled={!hasScheduledTime}
							/>
						</div>
					</div>

					{/* Predecessor task */}
					<div className="space-y-1.5">
						<Label htmlFor="predecessorTaskId">
							Predecessor task <span className="text-gray-400">(Optional)</span>
						</Label>
						<Select
							id="predecessorTaskId"
							value={predecessorTaskId}
							onChange={e => setPredecessorTaskId(e.target.value)}
							disabled={predecessorOptions.length === 0}
						>
							<option value="">None</option>
							{predecessorOptions.map((t: any) => (
								<option key={t._id} value={t._id}>
									{t.taskName}
								</option>
							))}
						</Select>
					</div>

					{/* Assigned To */}
					<div className="space-y-1.5">
						<Label htmlFor="assignedTo">Assigned To</Label>
						<Select
							id="assignedTo"
							value={assignedTo}
							onChange={e => setAssignedTo(e.target.value)}
						>
							{assignableUsers.map(user => (
								<option key={user.id} value={user.id}>
									{user.name}{user.id === userInfo?._id ? ' (me)' : ''}
								</option>
							))}
						</Select>
					</div>

					{/* Description */}
					<div className="space-y-1.5">
						<Label htmlFor="description">
							Description <span className="text-gray-400">(Optional)</span>
						</Label>
						<Textarea
							id="description"
							value={description}
							onChange={e => setDescription(e.target.value)}
							placeholder="Add any additional context or notes..."
							className="min-h-[80px] resize-none"
							rows={3}
						/>
					</div>

					{/* Actions */}
					<DialogFooter className="px-0 border-t border-gray-200 pt-4">
						<Button type="button" variant="ghost" onClick={handleClose}>
							Cancel
						</Button>
						<Button type="submit" disabled={!taskName.trim() || !projectid || isSubmitting}>
							<ListTodo className="w-4 h-4 mr-1" />
							{isSubmitting ? 'Creating...' : 'Create Task'}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>

		{duplicateMatch && (
			<DeletedDuplicateDialog
				open={duplicateDialogOpen}
				onClose={() => {
					setDuplicateDialogOpen(false)
					setDuplicateMatch(null)
					setPendingTaskPayload(null)
				}}
				itemType="task"
				matches={[duplicateMatch]}
				onRestore={handleRestoreExisting}
				onCreateNew={handleCreateNew}
				isLoading={isSubmitting}
			/>
		)}
		</>
	)
}

export default CreateTaskCNModal
